import type {
	LoginRequestDto,
	MfaVerifyRequestDto,
	RefreshResponseDto,
	RegisterRequestDto,
	RegisterResponseDto,
	UserDto,
} from "@babackapp/private-api-client/types";

export interface AuthErrorInfo {
	message: string;
	code?: string;
	status?: number;
}

export interface AuthState {
	user: UserDto | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	isAuthenticating: boolean;
	error: AuthErrorInfo | null;
}

// API Error types
export interface AuthValidationErrorItem {
	property: string;
	message: string;
	children: unknown[];
}

export interface AuthApiErrorBody {
	message: string | AuthValidationErrorItem[] | unknown;
	error?: string;
	statusCode?: number;
	code?: string;
	details?: unknown;
}

export interface AuthApiError {
	body?: AuthApiErrorBody;
	message?: string;
	status?: number;
}

export class AuthError extends Error {
	public readonly code: string;
	public readonly details?: unknown;
	public readonly status?: number;

	constructor(
		message: string,
		code: string = "UNKNOWN_ERROR",
		details?: unknown,
		status?: number,
	) {
		super(message);
		this.name = "AuthError";
		this.code = code;
		this.details = details;
		this.status = status;

		// Maintains proper stack trace for where our error was thrown
		if (
			"captureStackTrace" in Error &&
			Error.captureStackTrace &&
			typeof Error.captureStackTrace === "function"
		) {
			Error.captureStackTrace(this, AuthError);
		}
	}
}

// Refresh method type
export type RefreshMethod = () => Promise<RefreshResponseDto>;

/** An account with a second factor stops at `mfa_required`: finish with `verifyMfa`. */
export type LoginResult =
	| { status: "authenticated"; user: UserDto }
	| { status: "mfa_required"; mfaToken: string };

export interface AuthContextType extends AuthState {
	login: (credentials: LoginRequestDto) => Promise<LoginResult>;
	verifyMfa: (params: MfaVerifyRequestDto) => Promise<UserDto>;
	register: (data: RegisterRequestDto) => Promise<RegisterResponseDto>;
	logout: () => Promise<void>;
	refresh: RefreshMethod;
	verifyEmail: (token: string) => Promise<void>;
	resendVerification: (
		email: string,
	) => Promise<{ message: string; verificationEmailSent: boolean }>;
	forgotPassword: (email: string) => Promise<void>;
	resetPassword: (token: string, newPassword: string) => Promise<void>;
}

export interface TokenStorage {
	getAccessToken: () => string | null;
	setAccessToken: (token: string) => void;
	clearTokens: () => void;
}

export interface AuthConfig {
	// Interval in milliseconds to check token expiration
	checkTokenExpirationInterval: number;
	// Threshold in milliseconds before expiration to trigger refresh
	// Warning, this should be less than the token's actual expiration time,
	// and greater than the checkTokenExpirationInterval.
	// For example, if a token expires in 1 hour, you might set this to 5 minutes,
	// and checkTokenExpirationInterval to 1 minute. In this case, we will check each
	// minute if the token is about to expire in the next 5 minutes.
	checkTokenExpirationThreshold: number;
	// Threshold in milliseconds before expiration for request interceptor to refresh token
	// This is used by the token refresh interceptor to determine when to refresh tokens
	// before making API requests
	interceptorRefreshThreshold: number;
	// Delay in milliseconds before checking token when tab becomes visible
	// This ensures the tab is fully active before performing token operations
	tabVisibilityCheckDelay: number;
	// Enable debug logging for authentication operations
	debug: boolean;
}
